import { supabase } from '@/integrations/supabase/client';
import { notificationPreferencesService } from './notificationPreferencesService';

interface RolloverNotificationData {
  userId: string;
  userType: 'supabase' | 'internal';
  entityType: 'team' | 'venue' | 'coach';
  entityId: string;
  oldSeasonName: string;
  newSeasonName: string;
  status: 'scheduled' | 'complete' | 'failed';
  errorMessage?: string;
}

export const seasonRolloverNotificationService = {
  async sendRolloverNotification(data: RolloverNotificationData): Promise<void> {
    try {
      // Check if user has rollover notifications enabled
      const preferences = await notificationPreferencesService.getUserPreferences(
        data.userId,
        data.userType
      );
      
      const rolloverPref = preferences.find(p => p.notification_type === ('season_rollover' as any));
      if (!rolloverPref || !rolloverPref.enabled) {
        return; // User has disabled rollover notifications
      }

      let title = '';
      let message = '';

      switch (data.status) {
        case 'scheduled':
          title = 'Season Rollover Scheduled';
          message = `Your season rollover from "${data.oldSeasonName}" to "${data.newSeasonName}" has been scheduled`;
          break;
        case 'complete':
          title = 'Season Rollover Complete';
          message = `Successfully rolled over from "${data.oldSeasonName}" to "${data.newSeasonName}"`;
          break;
        case 'failed':
          title = 'Season Rollover Failed';
          message = `Failed to rollover from "${data.oldSeasonName}" to "${data.newSeasonName}". ${data.errorMessage || 'Please contact support'}`;
          break;
      }

      // Create in-app notification using existing enhanced notifications table
      await supabase.functions.invoke('create-enhanced-notification', {
        body: {
          user_id: data.userId,
          notification_type: 'season_rollover',
          category: 'system',
          title,
          message,
          priority: data.status === 'failed' ? 'high' : 'normal',
          action_required: data.status === 'failed',
          metadata: {
            entity_type: data.entityType,
            entity_id: data.entityId,
            old_season: data.oldSeasonName,
            new_season: data.newSeasonName,
            status: data.status
          }
        }
      });
    } catch (error) {
      console.error('Error sending rollover notification:', error);
    }
  },

  async notifyRolloverScheduled(
    userId: string,
    userType: 'supabase' | 'internal',
    entityType: 'team' | 'venue' | 'coach',
    entityId: string,
    oldSeasonName: string,
    newSeasonName: string,
    scheduledDate: string
  ): Promise<void> {
    await this.sendRolloverNotification({
      userId,
      userType,
      entityType,
      entityId,
      oldSeasonName,
      newSeasonName,
      status: 'scheduled'
    });
  },

  async notifyRolloverComplete(
    userId: string,
    userType: 'supabase' | 'internal',
    entityType: 'team' | 'venue' | 'coach',
    entityId: string,
    oldSeasonName: string,
    newSeasonName: string
  ): Promise<void> {
    // For teams, send batch notifications to all team members
    if (entityType === 'team') {
      await this.notifyTeamRolloverComplete(entityId, oldSeasonName, newSeasonName);
    } else {
      // For coaches and venues, send individual notification
      await this.sendRolloverNotification({
        userId,
        userType,
        entityType,
        entityId,
        oldSeasonName,
        newSeasonName,
        status: 'complete'
      });
    }
  },

  async notifyTeamRolloverComplete(
    teamId: string,
    oldSeasonName: string,
    newSeasonName: string
  ): Promise<void> {
    try {
      await supabase.functions.invoke('send-team-rollover-notifications', {
        body: {
          team_id: teamId,
          old_season_name: oldSeasonName,
          new_season_name: newSeasonName,
          status: 'complete'
        }
      });
    } catch (error) {
      console.error('Error sending team batch notifications:', error);
    }
  },

  async notifyRolloverFailed(
    userId: string,
    userType: 'supabase' | 'internal',
    entityType: 'team' | 'venue' | 'coach',
    entityId: string,
    oldSeasonName: string,
    newSeasonName: string,
    errorMessage?: string
  ): Promise<void> {
    // For teams, send batch notifications to all team members
    if (entityType === 'team') {
      await this.notifyTeamRolloverFailed(entityId, oldSeasonName, newSeasonName, errorMessage);
    } else {
      // For coaches and venues, send individual notification
      await this.sendRolloverNotification({
        userId,
        userType,
        entityType,
        entityId,
        oldSeasonName,
        newSeasonName,
        status: 'failed',
        errorMessage
      });
    }
  },

  async notifyTeamRolloverFailed(
    teamId: string,
    oldSeasonName: string,
    newSeasonName: string,
    errorMessage?: string
  ): Promise<void> {
    try {
      await supabase.functions.invoke('send-team-rollover-notifications', {
        body: {
          team_id: teamId,
          old_season_name: oldSeasonName,
          new_season_name: newSeasonName,
          status: 'failed',
          error_message: errorMessage
        }
      });
    } catch (error) {
      console.error('Error sending team batch notifications:', error);
    }
  }
};
